/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';

import type { RadioProps } from '../../types';
import RadioGroupContext from './RadioGroupContext';
import './Radio.less';

const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      value,
      children,
      description,
      className,
      disabled,
      onChange,
      checked,
      name,
      id,
      onMouseEnter,
      onMouseLeave,
      ...rest
    },
    ref
  ) => {
    const ctx = React.useContext(RadioGroupContext);

    const resolvedName = ctx?.name ?? name;
    const resolvedChecked = ctx ? ctx.selectedValue === value : checked;
    const resolvedDisabled = disabled ?? ctx?.disabled;
    const resolvedOnChange = ctx
      ? (e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.checked && value !== undefined) ctx.onGroupChange(value);
        }
      : onChange;

    const innerRef = React.useRef<HTMLInputElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      },
      [ref]
    );

    const circleAndInput = (
      <>
        <input
          ref={setRefs}
          id={id}
          type="radio"
          className="radio__input"
          name={resolvedName}
          value={value}
          checked={resolvedChecked}
          disabled={resolvedDisabled}
          onChange={resolvedOnChange}
          {...rest}
        />
        <span className="radio__box" aria-hidden="true" />
      </>
    );

    const modifiers = {
      'radio--checked': !!resolvedChecked,
      'radio--disabled': !!resolvedDisabled,
    };

    if (children) {
      return (
        <label
          className={cx('radio', modifiers, className)}
          onMouseEnter={(onMouseEnter as unknown) as React.MouseEventHandler<HTMLLabelElement>}
          onMouseLeave={(onMouseLeave as unknown) as React.MouseEventHandler<HTMLLabelElement>}>
          {circleAndInput}
          <span className="radio__text">
            <span className="radio__label">{children}</span>
            {description && <span className="radio__description">{description}</span>}
          </span>
        </label>
      );
    }

    return <span className={cx('radio', modifiers, className)}>{circleAndInput}</span>;
  }
);

Radio.displayName = 'Radio';

export default Radio;
